import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "../../hooks/useReveal";
import { ArrowRight, FileText } from "lucide-react";
import SampleRequestDialog from "../SampleRequestDialog";
import { useCupping } from "../../hooks/useCupping";

export default function RangeSection({ commodity, range, onContact }) {
  const [ref, visible] = useReveal();
  const [sampleLot, setSampleLot] = useState(null);
  const cupping = useCupping();
  return (
    <section className="bg-[#f6f1e7] py-24 md:py-32" data-testid="range-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div ref={ref} className={`reveal ${visible ? "in-view" : ""}`}>
          <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase mb-6">{range.index} · The Range</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0] text-[#10271a] max-w-2xl">
              {range.title} <em className="italic text-[#1f4a33]">{range.emphasis}</em>
            </h2>
            <p className="text-[#5e6b61] text-lg max-w-sm">{range.blurb}</p>
          </div>
        </div>
        <div className={`mt-16 grid grid-cols-1 md:grid-cols-2 ${range.items.length === 4 ? "xl:grid-cols-4" : "lg:grid-cols-3"} gap-8`}>
          {range.items.map((item, i) => (
            <RangeCard key={item.id} commodity={commodity} item={item} index={i} cupping={cupping[item.id]} onContact={onContact} onSample={() => setSampleLot(item)} />
          ))}
        </div>
      </div>
      <SampleRequestDialog open={!!sampleLot} onOpenChange={(v) => !v && setSampleLot(null)} commodity={commodity} lot={sampleLot} />
    </section>
  );
}

function RangeCard({ commodity, item, index, cupping, onContact, onSample }) {
  const [ref, visible] = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`range-card-${item.id}`}
      className={`group bg-[#fbf8f1] rounded-3xl overflow-hidden border border-[#10271a]/5 shadow-[0_18px_40px_-28px_rgba(16,39,26,0.5)] hover:shadow-[0_30px_60px_-28px_rgba(16,39,26,0.55)] transition-all duration-500 hover:-translate-y-1.5 reveal ${visible ? "in-view" : ""}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={item.image} alt={item.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
        <span className="absolute top-4 left-4 bg-[#10271a]/80 backdrop-blur text-[#f6f1e7] text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full">{item.tag}</span>
        <span className="absolute bottom-4 right-5 font-display text-5xl text-[#f6f1e7]/80">Ch. {index + 1}</span>
        {cupping && (
          <span className="absolute bottom-5 left-4 bg-[#f6f1e7]/95 text-[#10271a] rounded-full pl-3 pr-3.5 py-1.5 text-xs flex items-baseline gap-1.5 shadow" data-testid={`cupping-badge-${item.id}`}>
            <span className="font-display text-xl leading-none">{cupping.score}</span>
            <span className="text-[#5e6b61] text-[10px] tracking-[0.12em] uppercase">{cupping.scale}</span>
          </span>
        )}
      </div>
      <div className="p-7">
        <p className="text-[#7c5230] text-[11px] tracking-[0.18em] uppercase">{item.origin}</p>
        <h3 className="font-display text-3xl text-[#10271a] mt-2 leading-tight">{item.name}</h3>
        {item.wines && <p className="mt-2 text-[13px] text-[#1f4a33] font-medium tracking-wide" data-testid={`range-wines-${item.id}`}>{item.wines.join(" · ")}</p>}
        <p className="mt-4 text-[#5e6b61] leading-relaxed">{item.story}</p>
        {cupping?.notes && (
          <p className="mt-4 text-sm text-[#10271a]/80 leading-relaxed border-l-2 border-[#b08a5f] pl-4 italic" data-testid={`cupping-notes-${item.id}`}>
            "{cupping.notes}"<span className="not-italic block mt-1 text-[11px] text-[#5e6b61] tracking-[0.08em] uppercase">Cupped {cupping.cupped_on} · {cupping.cupper}</span>
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          {item.flavours.map((f) => (
            <span key={f} className="text-[11px] tracking-[0.08em] uppercase text-[#1f4a33] bg-[#1f4a33]/8 border border-[#1f4a33]/15 px-3 py-1 rounded-full">{f}</span>
          ))}
        </div>
        <button
          onClick={onSample}
          data-testid={`range-sample-${item.id}`}
          className="mt-7 w-full group/btn inline-flex items-center justify-center gap-2.5 bg-[#1f4a33] text-[#f6f1e7] px-6 py-3.5 rounded-full text-[13px] tracking-[0.1em] uppercase font-semibold hover:bg-[#10271a] transition-colors"
        >
          Request a sample
          <ArrowRight size={16} className="transition-transform group-hover/btn:translate-x-1" />
        </button>
        <div className="mt-4 flex items-center justify-between text-[12px] tracking-[0.1em] uppercase font-semibold">
          <Link to={`/spec/${commodity}/${item.id}`} data-testid={`range-spec-${item.id}`} className="inline-flex items-center gap-2 text-[#1f4a33] border-b border-[#1f4a33]/30 pb-0.5 hover:border-[#1f4a33] transition-colors">
            <FileText size={14} /> Lot spec sheet
          </Link>
          <button onClick={onContact} data-testid={`range-contact-${item.id}`} className="text-[#5e6b61] hover:text-[#10271a] transition-colors">Contact us</button>
        </div>
      </div>
    </article>
  );
}
